import type { CaseStatus, ChecklistSortField } from '@tms/shared';
import { CASE_STATUSES } from '@tms/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowDownUp,
  Columns3,
  Copy,
  FilterX,
  ListChecks,
  Plus,
  RotateCcw,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  CaseStatusBadge,
  EntityKey,
  ErrorState,
  LabelChip,
  Menu,
  MenuCheckboxItem,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuRadioGroup,
  MenuRadioItem,
  Pagination,
  SearchField,
  SkeletonRows,
  Table,
  TableWrap,
  ToolbarMenu,
  Td,
  Th,
  Tr,
} from '../../components/ui';
import { useEnumLabels, useErrorText, useFormat, useT, type MessageKey } from '../../i18n';
import { useDebounced } from '../../lib/use-debounced';
import { usePermissions } from '../auth/permissions';
import { FolderTree, type FolderSelection } from '../folders/FolderTree';
import { useLabels } from '../labels/api';
import { ChecklistFormDialog } from './ChecklistFormDialog';
import {
  CHECKLIST_COLUMNS,
  CHECKLIST_COLUMN_LABELS,
  useChecklistColumns,
} from './columns';
import { useChecklists, useDeleteChecklist, useDuplicateChecklist } from './api';

type SortValue = `${ChecklistSortField}:${'asc' | 'desc'}`;

/** Radix radio groups work on strings, so "no filter" needs its own value. */
const ANY = 'any';

/** The same ordering the test case list offers, over the checklist's own keys. */
const SORT_OPTIONS: Array<{ value: SortValue; labelKey: MessageKey }> = [
  { value: 'seqNumber:desc', labelKey: 'cases.sortNewest' },
  { value: 'seqNumber:asc', labelKey: 'cases.sortOldest' },
  { value: 'title:asc', labelKey: 'cases.sortTitleAsc' },
  { value: 'title:desc', labelKey: 'cases.sortTitleDesc' },
  { value: 'updatedAt:desc', labelKey: 'cases.sortUpdated' },
];

export const ChecklistsPage = ({ projectCode }: { projectCode: string }) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { t } = useT();
  const { relative } = useFormat();
  const { caseStatus } = useEnumLabels();
  const { errorText } = useErrorText();

  const columns = useChecklistColumns();
  const [folder, setFolder] = useState<FolderSelection>('all');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortValue>('seqNumber:desc');
  const [status, setStatus] = useState<CaseStatus | ''>('');
  const [labelId, setLabelId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; title: string; runCount: number } | null>(
    null,
  );

  const debouncedSearch = useDebounced(search);
  const [sortField, sortOrder] = sort.split(':') as [ChecklistSortField, 'asc' | 'desc'];
  const activeSort = SORT_OPTIONS.find((option) => option.value === sort) ?? SORT_OPTIONS[0]!;

  const { data, isPending, error, refetch } = useChecklists(projectCode, {
    page,
    ...(folder === 'all' ? {} : { folderId: folder, includeSubfolders: true }),
    ...(status ? { status } : {}),
    ...(labelId ? { labelId } : {}),
    ...(debouncedSearch ? { q: debouncedSearch } : {}),
    sort: sortField,
    order: sortOrder,
  });

  const activeFilterCount = (status ? 1 : 0) + (labelId ? 1 : 0);
  const { data: labels } = useLabels(projectCode);
  const duplicate = useDuplicateChecklist(projectCode);
  const remove = useDeleteChecklist(projectCode);

  const onDuplicate = async (checklistId: string) => {
    try {
      const copy = await duplicate.mutateAsync(checklistId);
      toast.success(t('checklists.duplicated', { key: copy.checklistKey }));
    } catch (duplicateError) {
      toast.error(errorText(duplicateError, 'checklists.duplicateFailed'));
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync(pendingDelete.id);
      toast.success(t('checklists.deleted'));
      setPendingDelete(null);
    } catch (deleteError) {
      toast.error(errorText(deleteError, 'checklists.deleteFailed'));
    }
  };

  return (
    <>
      <PageHeader
        title={t('checklists.title')}
        description={t('checklists.subtitle')}
        actions={
          can('checklist:write') ? (
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              {t('checklists.new')}
            </Button>
          ) : null
        }
      />

      <div className="flex flex-col gap-4 lg:flex-row">
        <FolderTree
          projectCode={projectCode}
          selected={folder}
          onSelect={(selection) => {
            setFolder(selection);
            setPage(1);
          }}
          total={data?.meta.total ?? 0}
          kind="checklist"
          allLabel={t('folders.allChecklists')}
        />

        <Card className="min-w-0 flex-1">
        <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
          <SearchField
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t('checklists.searchPlaceholder')}
            label={t('checklists.searchLabel')}
          />

          <ToolbarMenu
            icon={<SlidersHorizontal className="size-3.5" />}
            label={t('common.filters')}
            badge={activeFilterCount}
            compact
          >
            <MenuLabel>{t('common.status')}</MenuLabel>
            <MenuRadioGroup
              value={status === '' ? ANY : status}
              onValueChange={(value) => {
                setStatus(value === ANY ? '' : (value as CaseStatus));
                setPage(1);
              }}
            >
              <MenuRadioItem value={ANY} keepOpen>
                {t('cases.allStatuses')}
              </MenuRadioItem>
              {CASE_STATUSES.map((value) => (
                <MenuRadioItem key={value} value={value} keepOpen>
                  {caseStatus(value)}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>

            <MenuSeparator />

            <MenuLabel>{t('labels.title')}</MenuLabel>
            <MenuRadioGroup
              value={labelId === '' ? ANY : labelId}
              onValueChange={(value) => {
                setLabelId(value === ANY ? '' : value);
                setPage(1);
              }}
            >
              <MenuRadioItem value={ANY} keepOpen>
                {t('labels.allLabels')}
              </MenuRadioItem>
              {(labels ?? []).map((label) => (
                <MenuRadioItem key={label.id} value={label.id} keepOpen>
                  {label.name}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>

            {activeFilterCount > 0 ? (
              <>
                <MenuSeparator />
                <MenuItem
                  onSelect={() => {
                    setStatus('');
                    setLabelId('');
                    setPage(1);
                  }}
                >
                  <FilterX className="size-3.5" />
                  {t('common.resetFilters')}
                </MenuItem>
              </>
            ) : null}
          </ToolbarMenu>

          <ToolbarMenu
            icon={<Columns3 className="size-3.5" />}
            label={t('cases.columns')}
            compact
          >
            <MenuLabel>{t('cases.columns')}</MenuLabel>
            {CHECKLIST_COLUMNS.map((column) => (
              <MenuCheckboxItem
                key={column}
                checked={columns.isVisible(column)}
                onCheckedChange={(checked) => columns.toggle(column, checked)}
              >
                {t(CHECKLIST_COLUMN_LABELS[column])}
              </MenuCheckboxItem>
            ))}
            {columns.isDefault ? null : (
              <>
                <MenuSeparator />
                <MenuItem onSelect={columns.reset}>
                  <RotateCcw className="size-3.5" />
                  {t('cases.resetColumns')}
                </MenuItem>
              </>
            )}
          </ToolbarMenu>

          <ToolbarMenu
            icon={<ArrowDownUp className="size-3.5" />}
            label={t(activeSort.labelKey)}
            compact
          >
            <MenuLabel>{t('common.sort')}</MenuLabel>
            <MenuRadioGroup value={sort} onValueChange={(value) => setSort(value as SortValue)}>
              {SORT_OPTIONS.map((option) => (
                <MenuRadioItem key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
          </ToolbarMenu>
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : isPending ? (
          <SkeletonRows />
        ) : data.data.length === 0 ? (
          <EmptyState
            icon={<ListChecks className="size-6" />}
            title={debouncedSearch ? t('checklists.emptyFiltered') : t('checklists.empty')}
            description={
              debouncedSearch ? t('checklists.emptyFilteredHint') : t('checklists.emptyHint')
            }
            action={
              can('checklist:write') && !debouncedSearch ? (
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  <Plus className="size-4" />
                  {t('checklists.create')}
                </Button>
              ) : null
            }
          />
        ) : (
          <>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th className="w-28">{t('cases.columnKey')}</Th>
                    <Th className="w-full">{t('common.title')}</Th>
                    {columns.visible.map((column) => (
                      <Th
                        key={column}
                        className={column === 'items' || column === 'runs' ? 'text-right' : ''}
                      >
                        {t(CHECKLIST_COLUMN_LABELS[column])}
                      </Th>
                    ))}
                    <Th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((checklist) => (
                    <Tr
                      key={checklist.id}
                      onClick={() =>
                        void navigate({
                          to: '/projects/$projectCode/checklists/$checklistKey',
                          params: { projectCode, checklistKey: checklist.checklistKey },
                        })
                      }
                    >
                      <Td>
                        <EntityKey>{checklist.checklistKey}</EntityKey>
                      </Td>
                      <Td>
                        <Link
                          to="/projects/$projectCode/checklists/$checklistKey"
                          params={{ projectCode, checklistKey: checklist.checklistKey }}
                          className="font-medium text-ink hover:text-accent-fg"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {checklist.title}
                        </Link>
                        {checklist.description ? (
                          <p className="mt-0.5 line-clamp-1 max-w-md text-[12px] text-ink-subtle">
                            {checklist.description}
                          </p>
                        ) : null}
                      </Td>
                      {columns.isVisible('folder') ? (
                        <Td className="text-ink-muted">{checklist.folderName}</Td>
                      ) : null}
                      {columns.isVisible('status') ? (
                        <Td>
                          <CaseStatusBadge status={checklist.status} />
                        </Td>
                      ) : null}
                      {columns.isVisible('labels') ? (
                        <Td>
                          {checklist.labels.length === 0 ? (
                            <span className="text-ink-subtle">{t('common.empty')}</span>
                          ) : (
                            <span className="flex flex-wrap gap-1">
                              {checklist.labels.map((label) => (
                                <LabelChip key={label.id} label={label} />
                              ))}
                            </span>
                          )}
                        </Td>
                      ) : null}
                      {columns.isVisible('environment') ? (
                        <Td className="text-ink-muted">
                          {checklist.environment?.name ?? t('common.empty')}
                        </Td>
                      ) : null}
                      {columns.isVisible('layer') ? (
                        <Td className="text-ink-muted">
                          {checklist.layer?.name ?? t('common.empty')}
                        </Td>
                      ) : null}
                      {columns.isVisible('module') ? (
                        <Td className="text-ink-muted">
                          {checklist.module?.name ?? t('common.empty')}
                        </Td>
                      ) : null}
                      {columns.isVisible('items') ? (
                        <Td className="text-right tabular-nums">{checklist.itemCount}</Td>
                      ) : null}
                      {columns.isVisible('runs') ? (
                        <Td className="text-right tabular-nums">{checklist.runCount}</Td>
                      ) : null}
                      {columns.isVisible('author') ? (
                        <Td className="text-ink-muted">
                          {checklist.createdBy?.name ?? t('common.empty')}
                        </Td>
                      ) : null}
                      {columns.isVisible('updated') ? (
                        <Td className="text-ink-muted">{relative(checklist.updatedAt)}</Td>
                      ) : null}
                      <Td>
                        <div onClick={(event) => event.stopPropagation()}>
                          <Menu>
                            {can('checklist:write') ? (
                              <MenuItem onSelect={() => void onDuplicate(checklist.id)}>
                                <Copy className="size-3.5" />
                                {t('common.duplicate')}
                              </MenuItem>
                            ) : null}
                            {can('checklist:delete') ? (
                              <MenuItem
                                destructive
                                onSelect={() =>
                                  setPendingDelete({
                                    id: checklist.id,
                                    title: checklist.title,
                                    runCount: checklist.runCount,
                                  })
                                }
                              >
                                <Trash2 className="size-3.5" />
                                {t('common.delete')}
                              </MenuItem>
                            ) : null}
                          </Menu>
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            <Pagination meta={data.meta} onPageChange={setPage} />
          </>
        )}
        </Card>
      </div>

      <ChecklistFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        projectCode={projectCode}
        defaultFolderId={folder === 'all' ? undefined : folder}
        onCreated={(checklist) =>
          void navigate({
            to: '/projects/$projectCode/checklists/$checklistKey',
            params: { projectCode, checklistKey: checklist.checklistKey },
          })
        }
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={t('checklists.deleteTitle', { title: pendingDelete?.title ?? '' })}
        description={
          pendingDelete && pendingDelete.runCount > 0
            ? t('checklists.deleteHintRuns', {
                runs: t('projects.countRuns', { count: pendingDelete.runCount }),
              })
            : t('checklists.deleteHint')
        }
        confirmLabel={t('checklists.deleteAction')}
        loading={remove.isPending}
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
};
